'use client'

import { RouteModel } from '@polaris/core/models'
import { featureFlagsAtom } from '@polaris/core/stores'
import {
  computeCompoundSlippagePercent,
  computeMinAmountOutWithSlippage,
  formatCoin,
  formatDuration,
  formatPrice,
} from '@polaris/core/utils'
import { useFieldActions, useFieldValue, useForm } from 'form-atoms'
import { useAtomValue } from 'jotai'
import { ChevronRight, Info, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import {
  type DetailedHTMLProps,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useMeasure } from 'react-use'
import { Button } from '@/src/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/src/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/src/components/ui/dialog'
import { ListRowImage, ListRowSide } from '@/src/components/ui/list-row'
import { useFeesCategories } from '@/src/hooks/fees/use-fees-categories'
import { useOneTimeFeesTotal } from '@/src/hooks/fees/use-ne-time-fees-total'
import { useDisclosure } from '@/src/hooks/use-disclosure'
import { useSupportedChainsMap } from '@/src/queries/hooks/chains'
import { estimatedRemainingTimeTextAtom } from '@/src/stores/route-machine/atoms'
import {
  DEFAULT_SLIPPAGE,
  swapFormAtom,
  swapRouteAtom,
  swapRouteQueryEnabledAtom,
} from '@/src/stores/swap/atoms'
import { walletKeyringAtom } from '@/src/stores/wallets/atoms'
import { cn } from '@/src/utils/styles'
import { PriceDisplay } from '../price-display'
import { StepRow } from '../route-details/new/step-row'
import { StepLine } from '../route-details/step-line'
import { SubmitButton } from '../swap-form/submit-button'
import { Banner } from '../ui/banner'
import { ChainImg } from '../ui/chain-img'
import { Close } from '../ui/icons/close'
import { OneCircleFading } from '../ui/icons/one-circle-fading'
import { Skeleton } from '../ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'

interface RouteOverviewProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {}

export function RouteOverview(props: RouteOverviewProps) {
  const { feeCategorization } = useAtomValue(featureFlagsAtom)
  const { className, ...rest } = props
  const { data, error } = useAtomValue(swapRouteAtom)
  const isQueryEnabled = useAtomValue(swapRouteQueryEnabledAtom)
  const { addresses } = useAtomValue(walletKeyringAtom)
  const isAnyWalletConnected = Object.values(addresses).length > 0
  const estimatedTimeText = useAtomValue(estimatedRemainingTimeTextAtom)

  const routeModel = useMemo(
    () => (data?.route ? new RouteModel(data.route) : undefined),
    [data?.route],
  )

  const oneTimeFeesTotal = useOneTimeFeesTotal(routeModel)

  const {
    close: closeDialog,
    isOpen: isDialogOpen,
    onToggle: onDialogToggle,
  } = useDisclosure()

  const {
    close: closeTotalFeesSheet,
    isOpen: isTotalFeesSheetOpen,
    open: onTotalFeesSheetOpen,
  } = useDisclosure()

  // If there's no route model or data, handle loading and error states
  if (!routeModel || !data) {
    // Don't show loading skeleton when there's an error - let the error be displayed instead
    // Also don't show loading skeleton when query is disabled (e.g., only one asset selected)
    if (!error && isQueryEnabled) {
      return (
        <div className='flex justify-between items-baseline px-5 pt-2 pb-6'>
          <Skeleton className='h-4 w-32' />
          <Skeleton className='h-4 w-32' />
        </div>
      )
    }
    // If there's an error or query is disabled, don't render anything
    return null
  }

  const feesAvailable =
    data.route.AmountOut !== 0 &&
    isAnyWalletConnected &&
    routeModel.FeeRequirements !== undefined &&
    routeModel.FeeRequirements?.cost_check_result.fee.ticker !== ''

  return (
    <div
      className={cn(
        'flex items-baseline w-full justify-between px-5 min-h-8 mb-4 gap-2',
        className,
      )}
      {...rest}
    >
      <Dialog open={isDialogOpen} onOpenChange={onDialogToggle}>
        <DialogTrigger className='group py-1.5 relative'>
          <div className='flex items-center justify-center h-auto'>
            <span className='text-secondary text-sm font-normal group-hover:opacity-0 transition-opacity'>
              Best route{' '}
              <span className='text-link'>
                {formatDuration(routeModel.DurationInSeconds)}
              </span>
            </span>
          </div>
          <div className='flex items-center justify-center w-max h-auto group-hover:opacity-100 opacity-0 absolute left-0 inset-y-1.5 transition-opacity'>
            <span className='text-sm text-link-hover'>Show route</span>
          </div>
        </DialogTrigger>
        <DialogContent
          className='max-w-[384px] h-fit'
          showBackButton={false}
          showCloseButton={false}
        >
          <DialogTitle>
            <div className='flex items-center py-4 justify-between px-5 bg-foreground rounded-t-2xl h-20'>
              <div className='size-12 opacity-0' />
              <div className='flex flex-col text-center'>
                <h3 className='text-primary font-semibold text-xl'>
                  Route details
                </h3>
                {estimatedTimeText && (
                  <span className='text-secondary text-sm font-normal'>
                    Total estimated time{' '}
                    <span className='text-primary'>
                      {estimatedTimeText}
                    </span>{' '}
                  </span>
                )}
              </div>
              <Button
                type='button'
                onClick={closeDialog}
                size='sm'
                className='rounded-full size-6 pointer-events-auto mr-4'
                squared
              >
                <Close className='text-accent-primary' width={24} height={24} />
                <span className='sr-only'>Close</span>
              </Button>
            </div>
          </DialogTitle>
          <div className='px-8'>
            <PreviewFromChain route={routeModel} />
            <PreviewSteps route={routeModel} />
            <PreviewToChain route={routeModel} />
          </div>
          <div className='w-full pt-3 px-5 pb-5'>
            <SubmitButton onBeforeSubmit={closeDialog} />
          </div>
        </DialogContent>
      </Dialog>
      {feesAvailable ? (
        <div className='flex items-baseline'>
          <AnimatePresence>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              type='button'
              onClick={onTotalFeesSheetOpen}
              className='group flex items-baseline'
            >
              <div className='relative'>
                <div className='flex items-center justify-center'>
                  <span className='text-secondary text-sm font-normal group-hover:opacity-0 transition-opacity whitespace-nowrap'>
                    Total fees{' '}
                    <span className='text-link'>
                      {formatPrice(
                        routeModel.feeValueUsd -
                          (feeCategorization ? oneTimeFeesTotal : 0),
                      )}
                    </span>
                    {oneTimeFeesTotal > 0 && feeCategorization && (
                      <span className='text-link'>
                        {' '}
                        (+{formatPrice(oneTimeFeesTotal)})
                      </span>
                    )}
                  </span>
                </div>
                <div className='flex items-center justify-center w-max group-hover:opacity-100 opacity-0 absolute right-0 inset-y-1.5 transition-opacity'>
                  <span className='text-sm text-link-hover'>Show details</span>
                </div>
              </div>
            </motion.button>
          </AnimatePresence>
          {oneTimeFeesTotal > 0 && (
            <div className='ml-2'>
              <TooltipProvider>
                <OneTimeFeeBadge oneTimeFeesTotal={oneTimeFeesTotal} />
              </TooltipProvider>
            </div>
          )}
        </div>
      ) : (
        <span className='text-secondary text-sm font-normal'>
          1 {data.route.InputAssetMetadata.ticker} ≈{' '}
          <PriceDisplay
            price={
              data.route.InputAssetUSDPrice / data.route.OutputAssetUSDPrice
            }
            formatOptions={{
              style: 'decimal',
            }}
          />{' '}
          {data.route.OutputAssetMetadata.ticker}
        </span>
      )}
      <AnimatePresence mode='popLayout'>
        {isTotalFeesSheetOpen && (
          <TotalFees route={routeModel} onClose={closeTotalFeesSheet} />
        )}
      </AnimatePresence>
    </div>
  )
}

type TotalFeesView = 'default' | 'slippage-edit'

function TotalFees({
  route: routeModel,
  onClose,
}: {
  route: RouteModel
  onClose?: () => void
}) {
  const [selectedView, setSelectedView] = useState<TotalFeesView>('default')
  const [ref, { height }] = useMeasure()

  const content = useMemo(() => {
    switch (selectedView) {
      case 'default':
        return (
          <PreviewStats
            route={routeModel}
            setNewView={setSelectedView}
            closeModal={onClose}
          />
        )
      case 'slippage-edit':
        return <EditSlippage route={routeModel} setNewView={setSelectedView} />
    }
  }, [selectedView, routeModel, onClose])

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0, height }}
      transition={{ type: 'spring', visualDuration: 0.2, bounce: 0.3 }}
      className='absolute bottom-3 inset-x-3 z-30 rounded-2xl bg-surface2-100'
    >
      <div ref={ref as Ref<HTMLDivElement>}>
        <AnimatePresence>{content}</AnimatePresence>
      </div>
    </motion.div>
  )
}

function PreviewSteps({ route }: { route?: RouteModel }) {
  return route?.signSteps.map((step) => (
    <StepRow key={step.step_number} step={step} className='px-0' />
  ))
}

function PreviewStats({
  route,
  setNewView,
  closeModal,
}: {
  route: RouteModel
  setNewView: (view: TotalFeesView) => void
  closeModal?: () => void
}) {
  const { feeCategorization } = useAtomValue(featureFlagsAtom)
  const { fieldAtoms } = useForm(swapFormAtom)
  const slippage = useFieldValue(fieldAtoms.slippage)

  const { open, close, isOpen } = useDisclosure()

  return (
    <TooltipProvider>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className='flex flex-col'
      >
        <div className='flex flex-col p-4'>
          <PreviewItem
            name='Expected rate'
            value={
              <span className='text-primary text-sm'>
                1 {route.InputAssetMetadata.ticker} ≈{' '}
                <PriceDisplay
                  price={route.InputAssetUSDPrice / route.OutputAssetUSDPrice}
                  formatOptions={{
                    style: 'decimal',
                  }}
                />{' '}
                {route.OutputAssetMetadata.ticker}
              </span>
            }
            description='The expected rate indicates how much of one token you are expected to receive in exchange for a given amount of another token — not including any inclusive or additional fees.'
            valueDescription={`1 ${route.InputAssetMetadata.ticker} ≈ ${formatPrice(route.InputAssetUSDPrice)}`}
          />
          <button
            onClick={() => setNewView('slippage-edit')}
            className='flex justify-between items-center gap-2'
          >
            <PreviewItem
              name={
                <div className='flex items-center gap-1'>
                  <span className='text-sm text-secondary'>
                    Slippage tolerance
                  </span>
                </div>
              }
              value={
                <div className='flex items-center gap-2'>
                  {slippage === DEFAULT_SLIPPAGE && (
                    <div className='font-normal rounded-xl py-1 px-2 text-xs h-6 bg-surface2 text-secondary'>
                      Auto
                    </div>
                  )}
                  <span className='text-primary text-sm'>{slippage}%</span>
                </div>
              }
              description={
                "Slippage tolerance determines how much price movement you're willing to accept. If the price slips by more than this value, the trade will not be completed to protect you from receiving an undesired rate.\n\nCurrently slippage cannot be changed."
              }
            />
            <ChevronRight
              className='size-5 text-tertiary'
              width={24}
              height={24}
            />
          </button>
          <PreviewItem
            name={`Min ${route?.OutputAssetMetadata.ticker} received`}
            value={
              <>
                <span className='text-primary text-sm'>
                  <PriceDisplay
                    price={route.AmountOut}
                    formatOptions={{
                      style: 'decimal',
                    }}
                    zeroValue='0'
                  />{' '}
                  {route?.OutputAssetMetadata.ticker}{' '}
                </span>
                <span className='text-secondary text-sm'>
                  ({formatPrice(route.usdValueAmountOut)})
                </span>
              </>
            }
            description='The minimum amount you will receive if maximum slippage occurs.'
            valueDescription={`${formatPrice(route.usdValueAmountOut)}`}
          />
          {route.FeeRequirements?.cost_check_result.funds_required_display
            .usd && <FeeCollapsible route={route} />}
          {feeCategorization && (
            <div className='pt-2'>
              <Dialog open={isOpen} onOpenChange={open}>
                <DialogTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    size='lg'
                    className='rounded-xl p-3 w-full justify-between gap-3'
                  >
                    <Image
                      src='https://s3.polaris.app/illustrations/coin.png'
                      alt='Coin crate'
                      width={40}
                      height={40}
                    />
                    <div className='text-left'>
                      <p className='text-primary text-sm'>
                        Fees explained in simple terms
                      </p>
                      <p className='text-link text-sm'>Learn more</p>
                    </div>
                    <ChevronRight className='size-5 text-tertiary' size={20} />
                  </Button>
                </DialogTrigger>
                <DialogContent hideHeader className='p-0 max-w-[448px]'>
                  <FeesInfo close={close} />
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
        <div className='px-4 pt-1 pb-4'>
          <Button
            type='button'
            variant='secondary'
            size='lg'
            className='w-full text-base h-12'
            onClick={closeModal}
          >
            Close
          </Button>
        </div>
      </motion.div>
    </TooltipProvider>
  )
}

function PreviewItem({
  name,
  value,
  description,
  valueDescription,
}: {
  name: ReactNode
  value: ReactNode
  description: ReactNode
  valueDescription?: ReactNode
}) {
  return (
    <div className='flex-grow flex items-center py-1.5 justify-between gap-3'>
      <Tooltip delayDuration={100}>
        <TooltipTrigger type='button'>
          <span className='text-secondary text-sm'>{name}</span>
        </TooltipTrigger>
        {description && (
          <TooltipContent className='max-w-[280px]' hideArrow>
            <span className='text-xs inline-block text-secondary whitespace-break-spaces'>
              {description}
            </span>
          </TooltipContent>
        )}
      </Tooltip>
      <Tooltip delayDuration={100}>
        <TooltipTrigger type='button'>
          {typeof value === 'string' ? (
            <span className='text-primary text-sm'>{value}</span>
          ) : (
            value
          )}
        </TooltipTrigger>
        {valueDescription && (
          <TooltipContent className='max-w-[280px]' hideArrow>
            <span className='text-xs inline-block text-secondary whitespace-break-spaces'>
              {valueDescription}
            </span>
          </TooltipContent>
        )}
      </Tooltip>
    </div>
  )
}

function PreviewFromChain({ route }: { route: RouteModel }) {
  const firstFromChain = useMemo(
    () => route?.Steps[0].from_chain,
    [route?.Steps],
  )

  if (!firstFromChain) return

  return (
    <div className='flex items-start gap-2'>
      <div className='pr-3 flex flex-col items-center justify-end pt-6'>
        <ChainImg
          chainId={firstFromChain.chain_id}
          chainImg={firstFromChain.chain_img}
          chainName={firstFromChain.chain_name}
        />
        <StepLine className='h-6' />
      </div>
      <ListRowSide className='flex-row items-center gap-3 py-3'>
        <ListRowImage
          src={route.InputAssetMetadata.logo_img_url}
          width={40}
          height={40}
          className='size-10 rounded-full'
          unoptimized
        />
        <h6 className='text-primary font-semibold flex flex-col'>
          {route.InputAssetMetadata.ticker}{' '}
          <span
            data-testid='from-chain-name'
            className='font-normal text-sm leading-6 text-secondary'
          >
            on {firstFromChain.chain_name}
          </span>
        </h6>
      </ListRowSide>
    </div>
  )
}

function PreviewToChain({ route }: { route: RouteModel }) {
  const lastToChain = useMemo(
    () => route?.Steps.at(-1)?.to_chain,
    [route?.Steps],
  )

  if (!lastToChain) return

  return (
    <div className='flex items-start gap-2'>
      <div className='pr-3 flex flex-col items-center'>
        <StepLine className='h-6' />
        <ChainImg
          chainId={lastToChain.chain_id}
          chainImg={lastToChain.chain_img}
          chainName={lastToChain.chain_name}
        />
      </div>
      <ListRowSide className='flex-row items-center gap-3 py-3'>
        <ListRowImage
          src={route.OutputAssetMetadata.logo_img_url}
          width={40}
          height={40}
          className='size-10 rounded-full'
          unoptimized
        />
        <h6 className='text-primary font-semibold flex flex-col'>
          {route.OutputAssetMetadata.ticker}{' '}
          <span
            data-testid='from-chain-name'
            className='font-normal text-sm leading-6 text-secondary'
          >
            on {lastToChain.chain_name}
          </span>
        </h6>
      </ListRowSide>
    </div>
  )
}

const PRE_DEFINED_VALUES = [
  {
    value: 0.1,
  },
  {
    value: 0.25,
  },
  {
    value: 0.5,
  },
  {
    value: 1,
  },
  {
    value: 5,
  },
]

function EditSlippage({
  setNewView,
  route,
}: {
  route: RouteModel
  setNewView: (v: TotalFeesView) => void
}) {
  const { fieldAtoms } = useForm(swapFormAtom)
  const slippage = useFieldValue(fieldAtoms.slippage)
  const [dirty, setDirty] = useState(false)
  const { setValue: setSlippage } = useFieldActions(fieldAtoms.slippage)
  const [customSlippage, setCustomSlippage] = useState('')

  const isSlippageTooHigh = Number(customSlippage) > 10 && dirty
  const isSlippageTooLow = Number(customSlippage) < 0.1 && dirty

  const _onChange = useCallback(
    (value: string) => {
      if (
        Number(value) >= 100 ||
        Number(value) < 0 ||
        Number.isNaN(+value) ||
        value.split('.')[1]?.length > 2
      )
        return setSlippage('')

      setCustomSlippage(value)
      setDirty(true)
    },
    [setSlippage],
  )

  const done = useCallback(() => {
    if (dirty) {
      setSlippage(customSlippage)
    }
    setNewView('default')
  }, [customSlippage, dirty, setNewView, setSlippage])

  const { open, close, isOpen } = useDisclosure()

  const perStepSlippage =
    +customSlippage !== 0 ? +customSlippage : +slippage || 0

  const totalSlippage = useMemo(() => {
    const steps = route.Steps?.length ?? 0
    const value = computeCompoundSlippagePercent(perStepSlippage, steps)
    return Number.isFinite(value) ? Number(value.toFixed(2)) : 0
  }, [perStepSlippage, route.Steps])

  // Calculate minimum amount out based on compounded slippage (in percentage)
  const minAmountOut = useMemo(() => {
    const steps = route.Steps?.length ?? 0
    const minValue = computeMinAmountOutWithSlippage({
      amountOut: Number(route.AmountOut),
      perStepPercent: perStepSlippage,
      steps,
      minReceived: route.minReceived,
    })
    const fallback =
      route.minReceived ??
      (Number.isFinite(Number(route.AmountOut)) ? Number(route.AmountOut) : 0)
    return (minValue ?? fallback).toString()
  }, [perStepSlippage, route.AmountOut, route.Steps, route.minReceived])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className='flex flex-col'
    >
      <div className='flex flex-col gap-4 p-5'>
        <div className='flex flex-col gap-2'>
          <div className='flex items-center justify-between'>
            <span className='text-primary font-semibold'>
              Edit slippage tolerance
            </span>
            <Dialog open={isOpen} onOpenChange={open}>
              <DialogTrigger asChild>
                <Button type='button' variant='ghost'>
                  <Info className='text-link' />
                </Button>
              </DialogTrigger>
              <DialogContent hideHeader className='p-0 max-w-[448px]'>
                <SlippageInfo close={close} />
              </DialogContent>
            </Dialog>
          </div>
          <div className='flex flex-col gap-4'>
            {isSlippageTooHigh || isSlippageTooLow ? (
              <Banner
                variant={isSlippageTooHigh ? 'warning' : 'informative'}
                placement='inline'
                alertTitle={
                  isSlippageTooHigh
                    ? 'Your trade may result in significant loss of value'
                    : 'Your trade may fail'
                }
                description={
                  isSlippageTooLow
                    ? 'Try a higher slippage tolerance'
                    : undefined
                }
                primaryAction={
                  isSlippageTooLow
                    ? {
                        label: 'Set to 0.1%',
                        onClick: () => _onChange('0.1'),
                      }
                    : {
                        label: 'Set to 10%',
                        onClick: () => _onChange('10'),
                      }
                }
              />
            ) : (
              <span className='text-secondary text-sm leading-6'>
                Set the maximum amount of price movement (per trade step) you’re
                comfortable with. If the price slips further, your trade will
                not complete.
              </span>
            )}
            <div className='grid grid-cols-3 gap-2'>
              {PRE_DEFINED_VALUES.map(({ value }) => (
                <Button
                  type='button'
                  key={value}
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    setSlippage(value.toString())
                    setDirty(false)
                    setCustomSlippage('')
                  }}
                  className={cn('w-full', {
                    '!border-accent-primary border-2':
                      value === +slippage && !dirty,
                  })}
                >
                  {value}%
                </Button>
              ))}
              <button
                type='button'
                className='w-full px-3 py-1.5 rounded-2xl border border-button-outline-border flex items-center gap-2'
              >
                <input
                  type='text'
                  className='w-16 bg-transparent focus:outline-none text-sm placeholder:text-input-placeholder placeholder:text-left text-button-outline-text text-right'
                  placeholder='Custom'
                  value={customSlippage}
                  onChange={(e) => _onChange(e.target.value)}
                />
                <span className='text-secondary text-sm'>%</span>
              </button>
            </div>
          </div>
        </div>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between py-1.5'>
            <span className='text-secondary text-sm'>Receive at least</span>
            <span className='text-primary text-sm'>
              {formatCoin(
                {
                  amount: +minAmountOut,
                },
                {
                  maximumFractionDigits: route.OutputAssetMetadata.precision,
                  maximumSignificantDigits: 6,
                },
              )}{' '}
              {route.OutputAssetMetadata.ticker}{' '}
              <span className='text-secondary'>
                ({formatPrice(route.OutputAssetUSDPrice * +minAmountOut)})
              </span>
            </span>
          </div>
          <div className='flex items-center justify-between py-1.5'>
            <span className='text-secondary text-sm'>Total max slippage</span>
            <span className='text-primary text-sm'>
              <span>{totalSlippage}%</span>{' '}
              <span className='text-secondary'>
                (Across {route.Steps.length} trade
                {route.Steps.length > 1 ? 's' : ''})
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className='flex items-center gap-3 px-4 pb-4'>
        <Button
          onClick={() => setNewView('default')}
          type='button'
          variant='secondary'
          size='md'
          className='flex-1'
        >
          Cancel
        </Button>
        <Button
          type='button'
          size='md'
          variant='primary'
          onClick={done}
          className='flex-1'
        >
          Done
        </Button>
      </div>
    </motion.div>
  )
}

function SlippageInfo({ close }: { close: () => void }) {
  return (
    <div className='flex flex-col bg-foreground rounded-3xl overflow-hidden overflow-y-auto no-scrollbar'>
      <div className='flex items-center justify-center w-full pt-16 pb-4 px-[106px] bg-green-800 relative'>
        <Image src='/assets/boots.png' alt='' width={236} height={160} />
        <Button
          type='button'
          variant='secondary'
          size='sm'
          className='absolute top-6 right-6 size-8'
          onClick={close}
        >
          <X className='size-6 min-w-6 text-foreground' />
        </Button>
      </div>
      <div className='flex flex-col gap-8 p-8'>
        <div className='flex flex-col gap-4'>
          <h1 className='text-primary text-4xl font-extralight font-serif'>
            Slippage explained
          </h1>
          <p className='text-primary leading-7'>
            Imagine you’re at a supply store. You want to buy 1 candy bar, and
            the sign says it costs $1. You go to pay, but by the time the
            cashier rings it up, the price has changed to $1.05. You’re okay
            with that small difference, so you still buy it.
            <br />
            <br />
            That small extra 5 cents? That’s like{' '}
            <span className='font-semibold'>slippage</span> — a tiny change in
            price between the time you decide to trade and the time the trade
            actually happens. <br />
            <br /> <span className='font-semibold'>Slippage tolerance</span> is
            you saying: <br />
            <br /> “I’m okay with the price changing a little — but only up to a
            certain point.” <br />
            <br /> So if you set a 5% slippage tolerance, you’re saying: <br />
            <br /> “If the price goes up or down by more than 5%, don’t do the
            trade.”
            <br />
            <br /> This helps protect you from bad deals, especially when prices
            are moving fast or there’s not much candy (or crypto) to go around.
          </p>
        </div>
        <div className='flex flex-col gap-2'>
          <h4 className='text-primary font-semibold text-xl'>
            How does slippage work on Polaris?
          </h4>
          <p className='text-primary leading-7'>
            Since Polaris handles complex trades across networks, there are
            often a series of trades involved in any given trade route.
            Therefore, slippage tolerance applies{' '}
            <span className='font-semibold'>per step</span>. <br />
            <br /> For example, if you set a{' '}
            <span className='font-semibold'>5% slippage tolerance</span> on a
            route that involves three trades, you’re effectively accepting 5%
            slippage on{' '}
            <span className='font-semibold'>each separate trade</span>, which
            would result in a higher overall slippage of around{' '}
            <span className='font-semibold'>14.26%</span> in the worst case
            scenario.
          </p>
        </div>
        <Button
          type='button'
          variant='primary'
          className='w-full'
          onClick={close}
        >
          Done
        </Button>
      </div>
    </div>
  )
}

function FeesInfo({ close }: { close: () => void }) {
  return (
    <div className='flex flex-col bg-foreground rounded-3xl overflow-hidden overflow-y-auto no-scrollbar'>
      <div className='flex items-center justify-center w-full pt-16 pb-4 px-[106px] bg-orange-300 relative'>
        <Image
          src='https://s3.polaris.app/illustrations/coin-crate-02.png'
          alt='Coin crate'
          width={236}
          height={160}
        />
        <Button
          type='button'
          variant='secondary'
          size='sm'
          className='absolute top-6 right-6 size-8'
          onClick={close}
        >
          <X className='size-6 min-w-6 text-foreground' />
        </Button>
      </div>
      <div className='flex flex-col gap-8 p-8'>
        <div className='flex flex-col gap-4'>
          <h1 className='text-primary text-4xl font-extralight font-serif'>
            Fees explained
          </h1>
          <p className='text-primary leading-7'>
            When you trade crypto across different networks, there are a number
            of costs involved. Polaris makes this easy by bundling all costs
            into one payment at the start of a trade.
            <br />
            <br />
            The following explanations provide clarity on the different types of
            costs you may come across.
          </p>
        </div>
        <div className='flex flex-col gap-2'>
          <h4 className='text-primary font-semibold text-xl'>
            Inclusive vs additional fees
          </h4>
          <p className='text-primary leading-7'>
            Depending on the trade and the providers involved, some fees may be
            inclusive in the price you are quoted, while most others will be
            detailed and paid separately.
          </p>
        </div>
        <div className='flex flex-col gap-2'>
          <h4 className='text-primary font-semibold text-xl'>Network costs</h4>
          <p className='text-primary leading-7'>
            Almost every network charges fees to process transactions. These
            fees fluctuate depending on how busy the network is, and for some
            networks, can get quite expensive during these times. We’ll often
            warn you if we think the fees are high before you trade and we’ll
            include a buffer to account for any small fluctuations.{' '}
          </p>
        </div>
        <div className='flex flex-col gap-2'>
          <h4 className='text-primary font-semibold text-xl'>Polaris fee</h4>
          <p className='text-primary leading-7'>
            For more complex trades (especially those that span multiple
            networks), Polaris may charge a small fee to cover the
            infrastructure that powers these flows.{' '}
          </p>
        </div>
        <div className='flex flex-col gap-2'>
          <h4 className='text-primary font-semibold text-xl'>
            Account activation
          </h4>
          <p className='text-primary leading-7'>
            Some networks, like Solana, require a small balance to keep accounts
            active. You’ll only pay this fee if you’re trading on the network
            for the first time. Other providers, like CCTP (used to transfer
            USDC), charge a fee each time you perform a transfer.{' '}
          </p>
        </div>
        <div className='flex flex-col gap-2'>
          <h4 className='text-primary font-semibold text-xl'>
            Token activation{' '}
          </h4>
          <p className='text-primary leading-7'>
            On some networks, like Solana, a small one-time fee is required for
            each new token you receive.{' '}
          </p>
        </div>
        <div className='flex flex-col gap-2'>
          <h4 className='text-primary font-semibold text-xl'>Arrival boost </h4>
          <p className='text-primary leading-7'>
            If you trade to a network where you have insufficient funds to pay
            for fees, your received funds will get stuck. Fee forwarding
            conveniently solves this by (optionally) trading a little extra on
            your behalf to cover the cost of any additional transactions you may
            wish to do on the destination network.{' '}
          </p>
        </div>
        <Button
          type='button'
          variant='primary'
          className='w-full'
          onClick={close}
        >
          Done
        </Button>
      </div>
    </div>
  )
}

function FeeCollapsible({ route }: { route: RouteModel }) {
  const { feeCategorization } = useAtomValue(featureFlagsAtom)
  const { data: supportedChains } = useSupportedChainsMap()
  const {
    networkFees,
    solanaAccountActivationFees,
    solanaTokenActivationFees,
    polarisFees,
  } = useFeesCategories(route)
  const [feeNetworks, networkTotal] = useMemo(() => {
    const total = networkFees.reduce((acc, fee) => acc + fee.UsdEstimate, 0)
    const feeNetworks = new Set(networkFees.map((fee) => fee.ChainID)).values()
    return [feeNetworks, total]
  }, [networkFees])

  const solanaAccountActivation = useMemo(() => {
    const totalUSD = solanaAccountActivationFees.reduce(
      (acc, fee) => acc + fee.UsdEstimate,
      0,
    )
    const totalNative = solanaAccountActivationFees.reduce(
      (acc, fee) => acc + fee.Native.amount,
      0,
    )
    const ticker = solanaAccountActivationFees[0]?.Ticker
    const oneTimeFee =
      solanaAccountActivationFees.find((fee) => fee.one_off) !== undefined
    return {
      oneTimeFee,
      totalUSD,
      totalNative,
      ticker,
    }
  }, [solanaAccountActivationFees])

  const solanaTokenActivation = useMemo(() => {
    const totalUSD = solanaTokenActivationFees.reduce(
      (acc, fee) => acc + fee.UsdEstimate,
      0,
    )
    const totalNative = solanaTokenActivationFees.reduce(
      (acc, fee) => acc + fee.Native.amount,
      0,
    )
    const ticker = solanaTokenActivationFees[0]?.Ticker
    const oneTimeFee =
      solanaTokenActivationFees.find((fee) => fee.one_off) !== undefined
    return {
      oneTimeFee,
      totalUSD,
      totalNative,
      ticker,
    }
  }, [solanaTokenActivationFees])

  const polaris = useMemo(() => {
    const totalUSD = polarisFees.reduce((acc, fee) => acc + fee.UsdEstimate, 0)
    const totalNative = polarisFees.reduce(
      (acc, fee) => acc + fee.Native.amount,
      0,
    )
    const ticker = polarisFees[0]?.Ticker
    return {
      totalUSD,
      totalNative,
      ticker,
    }
  }, [polarisFees])

  const [isExpanded, setIsExpanded] = useState(false)

  if (!route.FeeRequirements) return null

  const totalFeeComponent = (
    <PriceRow
      name={`Total fees in ${route.FeeRequirements?.cost_check_result.fee.ticker}`}
      usd={route.feeValueUsd}
      extraValueSide={
        <Image
          src={route.FeeRequirements.cost_check_result.fee.logo_img_url}
          alt=''
          width={20}
          height={20}
          className='rounded-full'
        />
      }
      description='The total additional costs for the trade, comprised of network costs, some provider fees, and our fee. '
      isExpanded={isExpanded}
    />
  )

  if (
    !feeCategorization ||
    (networkFees.length === 0 &&
      solanaAccountActivationFees.length === 0 &&
      solanaTokenActivationFees.length === 0 &&
      polarisFees.length === 0)
  )
    return totalFeeComponent

  return (
    <Collapsible className='w-full' onOpenChange={setIsExpanded}>
      <CollapsibleTrigger>{totalFeeComponent}</CollapsibleTrigger>
      <CollapsibleContent>
        {networkTotal > 0 && (
          <PriceRow
            name={'Network costs'}
            usd={networkTotal}
            extraValueSide={
              <div className='flex'>
                {feeNetworks.map((chainId) => {
                  const chain = supportedChains?.get(chainId)
                  return (
                    <ChainImg
                      key={chainId}
                      chainId={chainId}
                      chainImg={chain?.chain_img}
                      chainName={chain?.chain_name}
                      containerClassName='-ml-3'
                      premultiply={true}
                    />
                  )
                })}
              </div>
            }
            description={
              <>
                <p className='mb-2'>
                  The total of all costs on each network involved throughout the
                  trade route.
                </p>
                <p className='mb-2'>
                  This amount fluctuates regularly depending on how busy the
                  networks are, so Polaris over-estimates to ensure your trade
                  is successful.
                </p>
                <p>
                  Any funds leftover will be reserved for your future trades
                  with the same networks.
                </p>
              </>
            }
          />
        )}
        {solanaAccountActivation.totalUSD > 0 && (
          <PriceRow
            name={'Solana account activation'}
            usd={solanaAccountActivation.totalUSD}
            extraValueSide={
              solanaAccountActivation.oneTimeFee ? (
                <OneTimeFeeBadge
                  oneTimeFeesTotal={solanaAccountActivation.totalUSD}
                />
              ) : null
            }
            description={
              <>
                <p className='mb-2'>
                  Some networks, like Solana, require a small balance to keep
                  accounts active. You’ll only pay this if you’re trading on the
                  network for the first time.
                </p>
                <p>
                  Other providers, like CCTP (used to transfer USDC), charge a
                  fee each time you perform a transfer.
                </p>
              </>
            }
          />
        )}
        {solanaTokenActivation.totalUSD > 0 && (
          <PriceRow
            name={'Solana token activation'}
            usd={solanaTokenActivation.totalUSD}
            extraValueSide={
              solanaTokenActivation.oneTimeFee ? (
                <OneTimeFeeBadge
                  oneTimeFeesTotal={solanaTokenActivation.totalUSD}
                />
              ) : null
            }
            description='On some networks, like Solana, a small one-time fee is required the first time you receive each individual token. This covers the cost of creating a dedicated token account on the network.'
          />
        )}
        {polaris.totalUSD > 0 && (
          <PriceRow
            name={'Polaris fee'}
            usd={polaris.totalUSD}
            description='For more complex trades (especially those that span multiple networks), Polaris may charge a small fee to cover the infrastructure that powers these flows.'
          />
        )}
        <div className='border-t-1 border-border1 mt-1' />
      </CollapsibleContent>
    </Collapsible>
  )
}

function PriceRow({
  name,
  usd,
  description,
  extraValueSide,
  isExpanded,
}: {
  name: string
  usd: number
  description: ReactNode
  extraValueSide?: ReactNode
  isExpanded?: boolean
}) {
  return (
    <PreviewItem
      name={<span className={isExpanded ? 'font-semibold' : ''}>{name}</span>}
      value={
        <div className='flex items-center gap-2'>
          {extraValueSide}
          <span className='text-primary text-sm'>{formatPrice(usd)}</span>
        </div>
      }
      description={description}
    />
  )
}

interface OneTimeFeeBadgeProps {
  oneTimeFeesTotal: number
}

function OneTimeFeeBadge({ oneTimeFeesTotal }: OneTimeFeeBadgeProps) {
  const { feeCategorization } = useAtomValue(featureFlagsAtom)
  if (!feeCategorization || oneTimeFeesTotal <= 0) return null
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <OneCircleFading className='text-link size-5 inline-block' />
      </TooltipTrigger>
      <TooltipContent>
        <div className='flex gap-3 text-xs'>
          <div>
            <OneCircleFading className='text-link size-5' />
          </div>
          <div>
            <p className='text-primary'>One-time fees included</p>
            <p className='text-secondary'>
              This trade includes one-time fees of{' '}
              {formatPrice(oneTimeFeesTotal)} that you won’t be charged again
              for the same or similar trades.
            </p>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
